import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy, ElementRef, HostListener, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true
    }
  ]
})
export class CustomDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy {

  // ========== Inputs ==========
  @Input() data: any[] = [];
  @Input() idField: string = 'id';
  @Input() textField: string = 'name';
  @Input() placeholder: string = 'Select...';
  @Input() multiSelect: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isLoading: boolean = false;           // Loading state from parent
  @Input() isLoadingMore: boolean = false;       // Loading more state from parent
  @Input() hasMore: boolean = true;              // Whether more items exist

  // Search settings
  @Input() showSearch: boolean = true;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() searchDebounceTime: number = 300;

  // Scroll settings
  @Input() maxHeight: number = 300;
  @Input() enableInfiniteScroll: boolean = false;

  // Appearance
  @Input() itemsShowLimit: number = 3;
  @Input() noResultsText: string = 'No results found';

  // ========== Outputs ==========
  @Output() selectionChange = new EventEmitter<any | any[]>();
  @Output() searchChange = new EventEmitter<string>();      // Emits when user types
  @Output() loadMore = new EventEmitter<void>();            // Emits when scroll reaches bottom

  // ========== Internal State ==========
  isOpen: boolean = false;
  selectedItems: any[] = [];
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.data = [...this.data];
    this.setupSearch();
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }  

  ngOnChanges(changes: SimpleChanges) {    
    if (changes['data'] && !changes['data'].firstChange) {      
      console.log("data changed and seletec items is ",this.selectedItems);
      if (this.selectedItems.length > 0) {                          
        this.addMissingItemsToData(this.selectedItems);
        this.onChange(this.multiSelect ? this.selectedItems : this.selectedItems[0]);
      }
    }
  }

  private setupSearch() {
    this.searchSubject.pipe(debounceTime(this.searchDebounceTime),distinctUntilChanged())
    .subscribe((searchTerm) => {
      // Emit to parent - parent will fetch data and update [data] input
      this.searchChange.emit(searchTerm);
    });
  }

  // ========== User Actions ==========
  onSearchInput() {
    this.searchSubject.next(this.searchTerm);
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
  }

  toggleItem(item: any) {
    if (this.disabled) return;

    if (this.multiSelect) {
      const index = this.selectedItems.findIndex(i => this.getId(i) === this.getId(item));
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
    } else {
      this.selectedItems = [item];
      this.isOpen = false;
    }

    this.onChange(this.multiSelect ? this.selectedItems : this.selectedItems[0]);
    this.selectionChange.emit(this.multiSelect ? this.selectedItems : this.selectedItems[0]);
  }

  removeItem(event: Event, item: any) {
    event.stopPropagation();
    const index = this.selectedItems.findIndex(i => this.getId(i) === this.getId(item));
    if (index !== -1) {
      this.selectedItems.splice(index, 1);
      this.onChange(this.multiSelect ? this.selectedItems : this.selectedItems[0]);
      this.selectionChange.emit(this.multiSelect ? this.selectedItems : this.selectedItems[0]);
    }
  }

  isSelected(item: any): boolean {
    return this.selectedItems.some(i => this.getId(i) === this.getId(item));
  }

  getId(item: any): any {
    return item[this.idField];
  }

  getDisplayValue(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item[this.textField] || '';
  }

  // ========== Infinite Scroll ==========
  onScroll(event: Event) {
    if (!this.enableInfiniteScroll) return;

    const container = event.target as HTMLElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when user scrolls to 80% of the list
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.8 && !this.isLoadingMore && this.hasMore) {
      this.loadMore.emit();
    }
  }

  // ========== Click Outside ==========
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  // ========== ControlValueAccessor Methods ==========  
  writeValue(value: any): void {
    console.log('writeValue called, data length:', this.data.length);
    
    if (value === null || value === undefined) {
      this.selectedItems = [];
      return;
    }
    
    let selectedValues: any[] = [];
    if (Array.isArray(value)) {
      selectedValues = value;
    } else {
      selectedValues = [value];
    }
    
    if (selectedValues.length === 0) {
      this.selectedItems = [];
      return;
    }
    
    // Check for missing items and add them to data
    const missingItems = this.addMissingItemsToData(selectedValues);
    
    if (missingItems.length > 0) {
      console.log(`Added ${missingItems.length} missing items to data`);
    }
    
    // Now match selected items (they should all exist in data)
    this.selectedItems = this.matchSelectedItems(selectedValues);
  }

addMissingItemsToData(selectedValues: any[]): any[] {
  const missing: any[] = [];
  
  for (const selected of selectedValues) {
    // Get the ID of the selected item
    let id: any;
    let fullItem: any = null;
    
    if (selected && typeof selected === 'object' && selected[this.idField]) {
      id = selected[this.idField];
      fullItem = selected;
    } else if (selected !== null && selected !== undefined) {
      id = selected;
    }
    
    if (id !== undefined) {
        // Check if item exists in current data
        const exists = this.data.some(item => this.getId(item) === id);
        
        if (!exists && fullItem) {
          // Add the full item to data
          this.data = [fullItem, ...this.data];
          missing.push(fullItem);
        } else if (!exists && fullItem === null) {
          // We have only ID but no full object - create placeholder
          const placeholder: any = { [this.idField]: id };
          placeholder[this.textField] = `Loading...`;
          this.data = [placeholder, ...this.data];
          missing.push(placeholder);                    
        }
      }
    }
  
    return missing;
  }

  private matchSelectedItems(selectedValues: any[]): any[] {
    const matched: any[] = [];
    
    for (const selected of selectedValues) {
      // Case: Selected is full object
      if (selected && typeof selected === 'object' && selected[this.idField]) {
        // Check if it exists in current data
        const existsInData = this.data.some(item => this.getId(item) === selected[this.idField]);
        if (existsInData) {
          matched.push(selected);
        }
      }
      // Case: Selected is just an ID
      else if (selected !== null && selected !== undefined) {
        const found = this.data.find(item => this.getId(item) === selected);
        if (found) {
          matched.push(found);
        }
      }
    }
    
    return matched;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
